import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaUser,
  FaCode,
  FaRocket,
  FaLightbulb,
  FaHandshake,
  FaArrowRight,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaCalendarAlt,
  FaBriefcase,
} from 'react-icons/fa';

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

const focuses = [
  {
    icon: FaCode,
    title: 'Clean Architecture',
    desc: 'Maintainable, scalable code with clear documentation.',
    color: 'text-cyan-400',
  },
  {
    icon: FaRocket,
    title: 'Performance',
    desc: 'Optimized for speed, efficiency, and seamless UX.',
    color: 'text-secondary',
  },
  {
    icon: FaLightbulb,
    title: 'Innovation',
    desc: 'Transforming complex problems into elegant solutions.',
    color: 'text-yellow-400',
  },
  {
    icon: FaHandshake,
    title: 'Collaboration',
    desc: 'Transparent communication and reliable delivery.',
    color: 'text-green-400',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const About: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section
      id="about"
      className="py-14 md:py-20 bg-gradient-to-b from-primary to-tertiary/30 relative overflow-hidden"
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {/* ── Header ── */}
          <motion.div variants={itemVariants} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/50 mb-5">
              {Icon(FaUser, 'text-xs text-secondary')}
              <span className="font-mono text-[11px] text-textSecondary/60 tracking-wider uppercase">
                About Me
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textPrimary">
              Behind The{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                Code
              </span>
            </h2>
          </motion.div>

          {/* ── Intro Card ── */}
          <motion.div
            variants={itemVariants}
            className="max-w-3xl mx-auto mb-10"
          >
            <div className="relative group">
              {/* Card glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/15 to-purple-500/15 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

              <div className="relative p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary/80 via-primary/60 to-primary/80 border border-border/40 backdrop-blur-xl">
                {/* Corner accents */}
                <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-blue-500/30 rounded-tl-lg" />
                <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-secondary/30 rounded-tr-lg" />
                <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-secondary/30 rounded-bl-lg" />
                <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-purple-500/30 rounded-br-lg" />

                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  {/* Left: Bio */}
                  <div className="flex-1 space-y-3">
                    <h3 className="text-xl md:text-2xl font-bold text-textPrimary">
                      Evode{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                        MUYISINGIZE
                      </span>
                      <span className="text-textSecondary font-normal text-base ml-2">(EvodeNuby)</span>
                    </h3>
                    <p className="text-secondary font-medium text-sm">
                      Full-Stack Developer
                    </p>
                    <p className="text-textSecondary text-sm leading-relaxed">
                      I'm <strong>Evode MUYISINGIZE</strong>, also known as <strong>Evode Nuby</strong>, a professional{' '}
                      <span className="text-cyan-400 font-medium">Full Stack Developer</span> and{' '}
                      <span className="text-secondary font-medium">Data Scientist</span> based in Kigali, Rwanda. 
                      I specialize in the{' '}
                      <span className="text-cyan-400 font-medium">MERN stack</span> and
                      am expanding into{' '}
                      <span className="text-secondary font-medium">data science & machine learning</span>.
                      I build scalable, efficient, and user-centered web applications with strong
                      attention to detail and clean architecture for clients across Rwanda and beyond.
                    </p>
                  </div>

                  {/* Right: Quick facts */}
                  <div className="md:w-52 flex flex-row flex-wrap md:flex-col gap-3 md:gap-2">
                    {[
                      { icon: FaMapMarkerAlt, label: 'Rwanda', color: 'text-cyan-400' },
                      { icon: FaGraduationCap, label: 'BSc. IT', color: 'text-secondary' },
                      { icon: FaCalendarAlt, label: '3+ Years', color: 'text-yellow-400' },
                      { icon: FaBriefcase, label: '7+ Projects', color: 'text-green-400' },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-tertiary/40 border border-border/30"
                      >
                        {Icon(item.icon, `text-xs ${item.color}`)}
                        <span className="text-xs font-medium text-textPrimary">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── Focus Areas ── */}
          <motion.div variants={itemVariants} className="max-w-5xl mx-auto">
            <h3 className="text-lg font-bold text-textPrimary text-center mb-8">
              What I{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                Bring
              </span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {focuses.map((f) => (
                <motion.div
                  key={f.title}
                  whileHover={{ y: -4 }}
                  className="group p-5 rounded-xl bg-primary/60 border border-border/40 backdrop-blur-md transition-all duration-300 hover:border-secondary/50 hover:shadow-lg hover:shadow-secondary/5"
                >
                  <div className={`${f.color} text-xl mb-3`}>{Icon(f.icon)}</div>
                  <h4 className="text-sm font-bold text-textPrimary mb-1.5">{f.title}</h4>
                  <p className="text-xs text-textSecondary leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── CTA ── */}
          <motion.div variants={itemVariants} className="text-center mt-14">
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-xs font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-secondary/30"
            >
              Let's Work Together
              {Icon(FaArrowRight, 'text-xs group-hover:translate-x-1 transition-transform')}
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
